import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams, Navigate, Link } from 'react-router-dom'
import { Shield, RefreshCw, ArrowLeft, Mail, Menu, X } from 'lucide-react'
import { useAuthStore } from '../store/authStore'

function maskEmail(email: string): string {
  const [local, domain] = email.split('@')
  if (!local || !domain) return email
  return `${local[0]}${'*'.repeat(Math.min(local.length - 1, 5))}@${domain}`
}

const ERROR_MESSAGES: Record<string, string> = {
  invalid_code: 'Incorrect code.',
  expired: 'Code has expired. Please request a new one.',
  too_many_attempts: 'Too many failed attempts. Please request a new code.',
  no_otp: 'Session expired. Please sign in again.',
  server_error: 'Something went wrong. Please try again.',
}

const TwoFactorPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const portal = searchParams.get('portal') as 'alumni' | 'admin' | 'employer' | null

  const { pendingOtpEmail, verifyOtp, resendOtp, cancelOtp } = useAuthStore()

  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', ''])
  const [trustDevice, setTrustDevice] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null)

  // Resend cooldown
  const [resendCooldown, setResendCooldown] = useState(0)
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const inputRefs = useRef<Array<HTMLInputElement | null>>([])

  const startCooldown = useCallback(() => {
    setResendCooldown(60)
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current!)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [])

  useEffect(() => {
    inputRefs.current[0]?.focus()
    startCooldown()
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current) }
  }, [startCooldown])

  const handleDigitChange = (idx: number, value: string) => {
    const char = value.slice(-1) // take last character typed
    if (char && !/^\d$/.test(char)) return // digits only

    const next = [...digits]
    next[idx] = char
    setDigits(next)
    setError(null)

    if (char && idx < 5) {
      inputRefs.current[idx + 1]?.focus()
    }
  }

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (digits[idx] !== '') {
        const next = [...digits]
        next[idx] = ''
        setDigits(next)
      } else if (idx > 0) {
        inputRefs.current[idx - 1]?.focus()
        const next = [...digits]
        next[idx - 1] = ''
        setDigits(next)
      }
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pasted.length === 0) return
    const next = ['', '', '', '', '', '']
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i]
    setDigits(next)
    const focusIdx = Math.min(pasted.length, 5)
    inputRefs.current[focusIdx]?.focus()
  }

  const clearDigits = () => {
    setDigits(['', '', '', '', '', ''])
    inputRefs.current[0]?.focus()
  }

  const handleSubmit = async (code: string) => {
    if (code.length !== 6 || loading) return
    setLoading(true)
    setError(null)

    const result = await verifyOtp(code, trustDevice)
    setLoading(false)

    if (!result.success) {
      clearDigits()
      const msg = ERROR_MESSAGES[result.error || 'server_error'] || ERROR_MESSAGES.server_error
      const fullMsg = result.attemptsLeft != null
        ? `${msg} ${result.attemptsLeft} attempt${result.attemptsLeft !== 1 ? 's' : ''} remaining.`
        : msg
      setError(fullMsg)
      setAttemptsLeft(result.attemptsLeft ?? null)

      // If session is totally gone, redirect to login
      if (result.error === 'no_otp' || result.error === 'too_many_attempts') {
        await cancelOtp()
        const loginPath = portal === 'admin' ? '/login/admin' : portal === 'employer' ? '/login/employer' : '/login'
        setTimeout(() => navigate(loginPath), 2000)
      }
      return
    }

    // Success — navigate based on role
    const { user } = useAuthStore.getState()
    if (!user) return

    // Wrong-portal enforcement
    if (portal && user.role !== portal) {
      await cancelOtp()
      const loginPath = portal === 'admin' ? '/login/admin' : portal === 'employer' ? '/login/employer' : '/login'
      navigate(loginPath)
      return
    }

    if (user.role === 'admin') {
      navigate('/admin')
    } else if (user.role === 'employer') {
      const s = (user.profile as any)?.approval_status
      if (s === 'pending') navigate('/employer/pending')
      else if (s === 'deactivated') navigate('/employer/deactivated')
      else navigate('/employer-dashboard')
    } else {
      const s = (user.profile as any)?.approval_status
      navigate(s === 'approved' ? '/dashboard' : '/pending-approval')
    }
  }

  const handleResend = async () => {
    if (resendCooldown > 0) return
    await resendOtp()
    clearDigits()
    setError(null)
    setAttemptsLeft(null)
    startCooldown()
  }

  const handleCancel = async () => {
    await cancelOtp()
    const loginPath = portal === 'admin' ? '/login/admin' : portal === 'employer' ? '/login/employer' : '/login'
    navigate(loginPath)
  }

  // Guard: must have arrived here via the login flow
  if (!pendingOtpEmail) {
    return <Navigate to="/login" replace />
  }

  const code = digits.join('')
  const isComplete = code.length === 6

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundImage: 'url(/uc_nobg.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>

      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2">
            <Link to="/" className="flex items-center">
              <img src="/trailblaze_logo.png" alt="Trailblaze" className="h-20" />
            </Link>

            <nav className="hidden md:flex space-x-8">
              <Link to="/news" className="text-gray-600 hover:text-gray-900">News & Updates</Link>
              <a href="#features" className="text-gray-600 hover:text-gray-900">Features</a>
              <a href="#benefits" className="text-gray-600 hover:text-gray-900">Benefits</a>
              <a href="#about" className="text-gray-600 hover:text-gray-900">About UCLM</a>
            </nav>

            <div className="hidden md:flex">
              <Link
                to="/login"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
              >
                Login
              </Link>
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6 text-gray-600" /> : <Menu className="w-6 h-6 text-gray-600" />}
            </button>
          </div>

          {isMobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <nav className="flex flex-col space-y-4">
                <Link to="/news" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>News & Updates</Link>
                <a href="#features" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>Features</a>
                <a href="#benefits" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>Benefits</a>
                <a href="#about" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>About UCLM</a>
                <Link to="/login" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors text-center" onClick={() => setIsMobileMenuOpen(false)}>Login</Link>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Page body */}
      <div className="flex flex-1 items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {/* Top accent */}
          <div className="h-1.5 w-full bg-gradient-to-r from-blue-700 via-blue-500 to-yellow-400" />

          <div className="p-8">
            {/* Icon + heading */}
            <div className="flex flex-col items-center text-center mb-8">
              <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-blue-700" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Verify Your Identity</h1>
              <p className="text-gray-500 text-sm mt-2 leading-relaxed">
                We sent a 6-digit verification code to
              </p>
              <div className="flex items-center gap-1.5 mt-1.5">
                <Mail className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-semibold text-blue-700">{maskEmail(pendingOtpEmail)}</span>
              </div>
            </div>

            {/* Digit inputs */}
            <div className="flex gap-2.5 justify-center mb-6" onPaste={handlePaste}>
              {digits.map((digit, idx) => (
                <input
                  key={idx}
                  ref={el => { inputRefs.current[idx] = el }}
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  value={digit}
                  onChange={e => handleDigitChange(idx, e.target.value)}
                  onKeyDown={e => handleKeyDown(idx, e)}
                  disabled={loading}
                  className={`w-11 h-14 text-center text-xl font-bold rounded-xl border-2 outline-none transition-all
                    ${digit ? 'border-blue-600 bg-blue-50 text-blue-900' : 'border-gray-200 bg-gray-50 text-gray-900'}
                    ${error ? 'border-red-400 bg-red-50' : ''}
                    focus:border-blue-600 focus:bg-blue-50
                    disabled:opacity-50 disabled:cursor-not-allowed`}
                />
              ))}
            </div>

            {/* Error message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-5 text-sm text-red-700 text-center">
                {error}
              </div>
            )}

            {/* Trust device checkbox */}
            <label className="flex items-center gap-3 mb-6 cursor-pointer select-none group">
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={e => setTrustDevice(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer"
              />
              <span className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors">
                Trust this device for <span className="font-medium">30 days</span>
              </span>
            </label>

            {/* Verify button */}
            <button
              onClick={() => handleSubmit(code)}
              disabled={!isComplete || loading}
              className="w-full py-3 bg-blue-900 hover:bg-blue-800 disabled:bg-gray-300 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify Code'
              )}
            </button>

            {/* Resend + cancel */}
            <div className="mt-5 flex flex-col items-center gap-3">
              <button
                onClick={handleResend}
                disabled={resendCooldown > 0 || loading}
                className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                {resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : "Didn't receive it? Resend code"}
              </button>

              <button
                onClick={handleCancel}
                disabled={loading}
                className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Cancel and return to login
              </button>
            </div>
          </div>
        </div>

        {/* Branding */}
        <p className="text-center text-xs text-gray-400 mt-5">
          Trailblaze Graduate Tracer System · UCLM
        </p>
      </div>
      </div>
    </div>
  )
}

export default TwoFactorPage
